// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

// Lightweight password strength gate (SEC-01). Because the password is the ONLY
// thing standing between a stolen server DB and the user's plaintext (it wraps
// the DEK via Argon2id), we enforce a real minimum here rather than pull in a
// multi-hundred-KB dictionary library. This is a coarse entropy estimate plus a
// few high-value rejections — not a substitute for a password manager, but it
// blocks the trivially crackable passwords that dominate real breaches.

const MIN_LENGTH = 12;

// A small set of the most-abused passwords / patterns. Not exhaustive by design;
// the length + character-class + repetition checks catch the long tail.
const COMMON = new Set([
  'password', 'passw0rd', 'password1', 'password123', '123456', '12345678',
  '123456789', '1234567890', 'qwerty', 'qwertyuiop', 'letmein', 'welcome',
  'admin', 'iloveyou', 'monkey', 'dragon', 'football', 'baseball', 'abc123',
  'trustno1', 'sunshine', 'princess', 'changeme', 'hushold', 'bitcoin',
]);

// Stable message codes (the i18n contract): components map these to translated
// strings under the `auth.passwordStrength` namespace. `message` stays as an
// English fallback for non-UI callers/tests.
export type PasswordMessageKey =
  | 'minLength'
  | 'tooCommon'
  | 'repeated'
  | 'needMix'
  | 'tooWeak'
  | 'strong'
  | 'good';

export interface PasswordStrength {
  ok: boolean;
  score: 0 | 1 | 2 | 3 | 4; // 0-4, matches the familiar zxcvbn scale
  message: string; // English reason/quality note (fallback; UI uses messageKey)
  messageKey: PasswordMessageKey; // translate as t(`auth:passwordStrength.${messageKey}`)
  messageParams?: { min: number }; // ICU args for the message (e.g. minLength)
}

function charClasses(pw: string): number {
  let classes = 0;
  if (/[a-z]/.test(pw)) classes++;
  if (/[A-Z]/.test(pw)) classes++;
  if (/[0-9]/.test(pw)) classes++;
  if (/[^A-Za-z0-9]/.test(pw)) classes++;
  return classes;
}

/** True when the string is a single repeated char or an obvious sequence. */
function isLowEntropyPattern(pw: string): boolean {
  if (/^(.)\1+$/.test(pw)) return true; // aaaaaa
  const sequences = ['abcdefghijklmnopqrstuvwxyz', '01234567890', 'qwertyuiop'];
  const lower = pw.toLowerCase();
  return sequences.some((seq) => seq.includes(lower) || seq.split('').reverse().join('').includes(lower));
}

export function evaluatePassword(pw: string): PasswordStrength {
  if (pw.length < MIN_LENGTH) {
    return {
      ok: false,
      score: 0,
      message: `Password must be at least ${MIN_LENGTH} characters.`,
      messageKey: 'minLength',
      messageParams: { min: MIN_LENGTH },
    };
  }
  const normalized = pw.toLowerCase();
  if (COMMON.has(normalized)) {
    return { ok: false, score: 0, message: 'That password is too common. Choose something unique.', messageKey: 'tooCommon' };
  }
  if (isLowEntropyPattern(pw)) {
    return { ok: false, score: 0, message: 'Avoid repeated characters or keyboard sequences.', messageKey: 'repeated' };
  }

  const classes = charClasses(pw);
  const uniqueChars = new Set(pw).size;

  // Coarse entropy: length weighted by character diversity.
  const poolSize = [0, 10, 26, 36, 62, 95][Math.min(classes + 1, 5)] || 26;
  const bits = Math.log2(poolSize) * Math.min(pw.length, 40) * (uniqueChars / pw.length);

  let score: PasswordStrength['score'] = 0;
  if (bits >= 90) score = 4;
  else if (bits >= 70) score = 3;
  else if (bits >= 50) score = 2;
  else if (bits >= 35) score = 1;

  if (classes < 2) {
    return {
      ok: false,
      score: score > 1 ? 1 : score,
      message: 'Use a mix of at least two of: lowercase, uppercase, numbers, symbols.',
      messageKey: 'needMix',
    };
  }
  if (score < 3) {
    return {
      ok: false,
      score,
      message: 'Password is too weak — make it longer or more varied.',
      messageKey: 'tooWeak',
    };
  }

  return score === 4
    ? { ok: true, score, message: 'Strong password.', messageKey: 'strong' }
    : { ok: true, score, message: 'Good password.', messageKey: 'good' };
}
